import React, { createContext, useContext, useMemo } from 'react';
import { can, hasRole } from './permissions';

// Roles for the signed-in user, seeded from the login result.
//
// NB seeded from `authService.validate()`, which reads roles from the ACCESS token. Do NOT source them
// from `authService.getUser()`, which reads `idTokenClaims.roles` — Entra does not reliably put app
// roles in the ID token, so that path can report an empty roles array for a legitimate admin and hide
// controls the user is entitled to.

// Default must be SHAPE-COMPLETE — every capability present and returning false. A component rendered
// outside the provider (a test, a future route) would otherwise call an undefined function and crash,
// rather than simply showing its controls disabled. Fail closed, not fail hard.
const NO_CAPABILITIES = {
    roles: [],
    hasRole: () => false,
    can: Object.fromEntries(Object.keys(can).map(k => [k, () => false])),
};

const AuthContext = createContext(NO_CAPABILITIES);

export function AuthProvider({ user, children }) {
    const roles = useMemo(() => (Array.isArray(user?.roles) ? user.roles : []), [user]);
    const value = useMemo(() => ({
        roles,
        hasRole: role => hasRole(roles, role),
        // Bind roles so components read `can.releaseModel()` with no argument.
        can: Object.fromEntries(Object.entries(can).map(([k, fn]) => [k, () => fn(roles)])),
    }), [roles]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Capabilities of the signed-in user, e.g. `const { can } = useAuth(); can.releaseModel()`. */
export function useAuth() {
    return useContext(AuthContext);
}
